import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { isForbidden, isUnauthorized } from '@banking/api/utils/auth-errors';
import multipassStatus from '@banking/api/utils/multipass-status';
import { replace } from '@square/dashboard-utilities/utils/window';
import * as Sentry from '@sentry/ember';
import { LAUNCH_DARKLY_FEATURE_FLAGS } from '../utils/launch-darkly-feature-flags';

export default class ApplicationRoute extends Route {
  @service eventstream;
  @service dataApi;
  @service features;
  @service capitalEngineContext;
  @service sentry;

  async model() {
    const mpStatus = await multipassStatus();
    const { merchantToken, personToken, employmentToken } = mpStatus;
    // Currently we only onboard existing merchants to Savings. However, the merchantToken
    // can be null if the merchant hasn't signed in (or signed up) yet, prior to being redirected.
    // In that case, we'd ideally use an anonymous visitor token. But since all the interesting tracking is
    // happening post login, we're leaving that as a TODO
    // TODO anonymous visitor token
    if (merchantToken) {
      await this.dataApi.identify({
        merchantToken,
        personToken,
        employmentToken,
      });
    }

    return mpStatus;
  }

  async afterModel({ merchantToken, personToken, employmentToken }) {
    await this._setupLaunchDarklyFeatureFlags(merchantToken);

    const client = this.eventstream.client;
    // NOTE: subject_anonymous_token is pulled from _savt cookie automatically
    client.merchantId = merchantToken; // used as subject_merchant_token
    client.personId = personToken; // used as subject_person_token
    client.employmentId = employmentToken; // used as subject_employment_token
    // TODO: session_id?

    Sentry.setUser({ id: merchantToken });

    this.capitalEngineContext.sessionContext = {
      employmentToken,
      merchantToken,
      personToken,
    };
  }

  redirect(_, transition) {
    if (transition.to.name === 'index') {
      this.replaceWith('signup');
    }
  }

  async _setupLaunchDarklyFeatureFlags(merchantToken) {
    try {
      // This user country attribute is used in LD. Savings is US only
      const attributes = {
        country: 'US',
      };

      if (this.features.isSetup()) {
        await this.features.evaluate(
          LAUNCH_DARKLY_FEATURE_FLAGS,
          merchantToken,
          attributes,
          true
        );
      } else {
        await this.features.evaluateAndSetup(
          LAUNCH_DARKLY_FEATURE_FLAGS,
          merchantToken,
          attributes
        );
      }
    } catch {
      // noop
    }
  }

  // error hook will be called if any model promises are rejected
  // never call this directly using this.send('error', error) in a route
  // because it is only meant to be used as a hook. instead use handleRpcError.
  @action error(error) {
    if (isUnauthorized(error)) {
      // Unauthorized, almost certainly you need to log in:
      replace(
        `/login?return_to=${window.encodeURIComponent(
          window.location.pathname
        )}`
      );
      return false;
    } else if (isForbidden(error)) {
      // Forbidden, almost certainly you're not an account owner:
      this.intermediateTransitionTo('forbidden');
      return false;
    } else {
      // let the error bubble and use default error substate
      return true;
    }
  }
}
